import { FC } from "react";

import { Box, Chip, Typography } from "@mui/material";
import { MeetingLifecycle } from "@rubi/api-generated/clientRs";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

// Wording follows the subject mockup and the requirements doc's Key System
// States for the export tail.
const LABEL_KEYS: Record<string, string> = {
  created: "lifecycle.status.created",
  invitation_created: "lifecycle.status.invitationCreated",
  invitation_sent: "lifecycle.status.invitationSent",
  link_accessed: "lifecycle.status.linkAccessed",
  waiting_room: "lifecycle.status.waitingRoom",
  in_progress: "lifecycle.status.inProgress",
  recording_in_progress: "lifecycle.status.recordingInProgress",
  ended: "lifecycle.status.ended",
  export_pending: "lifecycle.status.exportPending",
  export_successful: "lifecycle.status.exportSuccessful",
  export_failed: "lifecycle.status.exportFailed",
  re_export_requested: "lifecycle.status.reExportRequested",
  marked_for_purge: "lifecycle.status.markedForPurge",
  purged: "lifecycle.status.purged",
};

const COLORS: Record<
  string,
  "default" | "primary" | "success" | "error" | "warning" | "info"
> = {
  waiting_room: "warning",
  in_progress: "success",
  recording_in_progress: "error",
  export_pending: "info",
  export_successful: "success",
  export_failed: "error",
  re_export_requested: "warning",
  marked_for_purge: "default",
  purged: "default",
};

export const lifecycleLabel = (status: string, t: TFunction): string =>
  LABEL_KEYS[status] ? t(LABEL_KEYS[status]) : status;

interface Props {
  lifecycle: MeetingLifecycle;
  size?: "small" | "medium";
  showPurge?: boolean;
}

// Detail is visible text, not a tooltip: a Chip takes no focus, so a tooltip
// would be keyboard-unreachable and its title would displace the lifecycle
// label in the accessible name.
const LifecycleChip: FC<Props> = ({
  lifecycle,
  size = "small",
  showPurge = true,
}) => {
  const { t } = useTranslation("connect");
  const parts: string[] = [];
  if (lifecycle.waiting_count > 0)
    parts.push(t("lifecycle.waiting", { count: lifecycle.waiting_count }));
  if (lifecycle.admitted_count > 0)
    parts.push(t("lifecycle.inRoom", { count: lifecycle.admitted_count }));
  if (showPurge && lifecycle.purge_after)
    parts.push(
      t("lifecycle.purges", {
        date: new Date(lifecycle.purge_after).toLocaleDateString(),
      }),
    );
  const label = lifecycle.paused
    ? t("lifecycle.paused")
    : lifecycleLabel(lifecycle.status, t);
  const color = lifecycle.paused ? "warning" : COLORS[lifecycle.status];
  const chip = (
    <Chip
      size={size}
      color={color ?? "default"}
      variant={color ? "filled" : "outlined"}
      label={label}
    />
  );
  if (!parts.length) return chip;
  return (
    <Box sx={{ minWidth: 0 }}>
      {chip}
      <Typography variant="caption" color="text.secondary" display="block">
        {parts.join(" · ")}
      </Typography>
    </Box>
  );
};

export default LifecycleChip;
